import { useCallback, useEffect, useMemo } from 'react';

interface TelegramWebApp {
  ready: () => void;
  expand: () => void;
  close: () => void;
  isExpanded: boolean;
  colorScheme: 'light' | 'dark';
  themeParams: Record<string, string>;
  initData: string;
  initDataUnsafe: Record<string, any>;
  BackButton: {
    show: () => void;
    hide: () => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
    isVisible: boolean;
  };
  MainButton: {
    text: string;
    color: string;
    textColor: string;
    isVisible: boolean;
    isActive: boolean;
    show: () => void;
    hide: () => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
    setText: (text: string) => void;
    setParams: (params: Record<string, any>) => void;
  };
  HapticFeedback: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
    selectionChanged: () => void;
  };
  openLink: (url: string) => void;
  openTelegramLink: (url: string) => void;
}

const getTelegramWebApp = (): TelegramWebApp | null => {
  try {
    return (window as any).Telegram?.WebApp || null;
  } catch {
    return null;
  }
};

export const useTelegram = () => {
  const tg = useMemo(() => getTelegramWebApp(), []);

  useEffect(() => {
    try {
      if (tg) {
        tg.ready();
        tg.expand();
      }
    } catch {}
  }, [tg]);

  const hapticImpact = useCallback(
    (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' = 'medium') => {
      try { tg?.HapticFeedback?.impactOccurred(style); } catch {}
    },
    [tg]
  );

  const hapticNotification = useCallback(
    (type: 'error' | 'success' | 'warning') => {
      try { tg?.HapticFeedback?.notificationOccurred(type); } catch {}
    },
    [tg]
  );

  const hapticSelection = useCallback(() => {
    try { tg?.HapticFeedback?.selectionChanged(); } catch {}
  }, [tg]);

  const showBackButton = useCallback(
    (onBack: () => void) => {
      try {
        if (tg?.BackButton) {
          tg.BackButton.show();
          tg.BackButton.onClick(onBack);
          return () => {
            try {
              tg.BackButton.offClick(onBack);
              tg.BackButton.hide();
            } catch {}
          };
        }
      } catch {}
      return () => {};
    },
    [tg]
  );

  const openLink = useCallback(
    (url: string) => {
      try {
        if (tg) {
          tg.openLink(url);
        } else {
          window.open(url, '_blank');
        }
      } catch {
        window.open(url, '_blank');
      }
    },
    [tg]
  );

  const openTelegramLink = useCallback(
    (url: string) => {
      try {
        if (tg) {
          tg.openTelegramLink(url);
        } else {
          window.open(url, '_blank');
        }
      } catch {
        window.open(url, '_blank');
      }
    },
    [tg]
  );

  return {
    tg,
    user: tg?.initDataUnsafe?.user,
    colorScheme: tg?.colorScheme || 'light',
    hapticImpact,
    hapticNotification,
    hapticSelection,
    showBackButton,
    openLink,
    openTelegramLink,
  };
};
